import React, { useEffect, useState } from "react";
import { RadioButton, RadioButtonGroup } from "carbon-components-react";
import { FormikErrors } from "formik";

import { SandboxRequestPercentOrValue } from "@eave/shared/types/api";

import DollarInput, { DollarInputProps } from "web_modules/shared/carbon/DollarInput";
import PercentageInput, { PercentageInputProps } from "web_modules/shared/carbon/PercentageInput";

const styles = require("./styles.module.scss");

enum InputMode {
  Dollar = "dollar",
  Percentage = "percentage",
}

const formToInputMode = (pctOrVal: SandboxRequestPercentOrValue): InputMode => {
  if (pctOrVal.value !== null) {
    return InputMode.Dollar;
  }
  if (pctOrVal.percent !== null) {
    return InputMode.Percentage;
  }
  return InputMode.Dollar; // default, to be consistent with backend handling of combo type
};

type Exclude = "value" | "invalid" | "invalidText";

interface Props extends Omit<DollarInputProps & PercentageInputProps, Exclude> {
  percentOrValue: SandboxRequestPercentOrValue;
  errors: FormikErrors<SandboxRequestPercentOrValue> | undefined;
  updateInputMode(name: string, mode: InputMode): void;
}

const DollarPercentageInput = (props: Props) => {
  const {
    id,
    name,
    onChange,
    onBlur,
    percentOrValue,
    errors,
    updateInputMode,
    ...otherProps
  } = props;

  const [inputMode, setInputMode] = useState<InputMode>(formToInputMode(percentOrValue));

  useEffect(() => {
    updateInputMode(name, inputMode);
  }, []);

  const hasValueErrors = errors && !!errors.value;
  const hasPercentErrors = errors && !!errors.percent;

  // HACK: jjw: normally we'd let these remain blank and have validation enforce putting a value
  // here, but writing the formik validation for the combo input is difficult if they're not both
  // nullable - as a hack, since we don't want them to appear blank in the UI, we set to zero
  const changeToZero = (e: React.FocusEvent<HTMLInputElement>, name: string) => {
    if (e.target.value === "") {
      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: "0",
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      {inputMode === InputMode.Dollar && (
        <DollarInput
          {...otherProps}
          id={`${id}-value`}
          name={`${name}.value`}
          value={percentOrValue.value}
          invalid={hasValueErrors}
          invalidText={errors && errors.value}
          onChange={onChange}
          onBlur={e => changeToZero(e, `${name}.value`)}
        />
      )}
      {inputMode === InputMode.Percentage && (
        <PercentageInput
          {...otherProps}
          id={`${id}-percent`}
          name={`${name}.percent`}
          value={percentOrValue.percent}
          invalid={hasPercentErrors}
          invalidText={errors && errors.percent}
          onChange={onChange}
          onBlur={e => changeToZero(e, `${name}.percent`)}
        />
      )}
      <div className={styles.radio}>
        <RadioButtonGroup
          id={`${id}-radio-button-group`}
          name={`${name}_radio_button_group`}
          valueSelected={inputMode}
          disabled={otherProps.disabled || hasValueErrors || hasPercentErrors}
          onChange={(m: InputMode) => {
            setInputMode(m);
            updateInputMode(name, m);
          }}
        >
          <RadioButton
            id={`${id}-radio-button-dollar`}
            labelText="Use dollars"
            value={InputMode.Dollar}
          />
          <RadioButton
            id={`${id}-radio-button-percentage`}
            labelText="Use percentage of purchase price"
            value={InputMode.Percentage}
          />
        </RadioButtonGroup>
      </div>
    </div>
  );
};

export { InputMode as DollarPercentageInputMode };

export default DollarPercentageInput;
